// 261010 UI1a — tầng 1 bảng cấp tập của khung 8 bước: đầu trang gọn (một dòng tóm tắt + nút chính) · thanh 8 ô · nội dung bước đang chọn (+ khối CapCut ở bước Video).
// Không còn "Chặng n/3", băng Máy tự chạy, ô Ghép, lưới thẻ / timeline Ghép (file card / timeline còn nguyên cho route cũ `?step=ASSEMBLY`).
import React, { useEffect, useRef, useState } from 'react';
import type { EventPlan, FilmEvent, PositionMapsResponse, StoryboardListItem } from '../../types/film';
import type { WorkflowStatus, WorkflowStepKey } from '../../types/filmWorkflow';
import type { ActionError } from './workflowActionPolicy';
import type { BoardFilter } from './workflowBoardUtils';
import { boardStepOf, primaryActionOf, stepHeaderActionsOf, type PrimaryCall } from './workflowPrimaryPolicy';
import { stuckAfter, sentinelRatioOf, STICKY_HYST_PX, UNSTICK_DEBOUNCE_MS } from './workflowStickyPolicy';
import { StepErrorAlert } from './WorkflowStepShared';
import WorkflowHero from './WorkflowHero';
import { WorkflowTopExtraContext } from './WorkflowTopExtraContext';
import WorkflowPrimaryBar from './WorkflowPrimaryBar';
import WorkflowStepsBar from './WorkflowStepsBar';
import WorkflowStepBoard from './WorkflowStepBoard';

interface Props {
  top: React.ReactNode;
  status: WorkflowStatus;
  planName: string;
  planId: string;
  storyId: string;
  filter: BoardFilter;
  setFilter: React.Dispatch<React.SetStateAction<BoardFilter>>;
  detail: EventPlan | null;
  detailLoading: boolean;
  detailError: string | null;
  onLoadDetail: () => void;
  storyboards: StoryboardListItem[] | null;
  storyboardsError: string | null;
  maps: PositionMapsResponse | null;
  mapsError: string | null;
  eventsById: Map<string, { ev: FilmEvent; sceneId: string }>;
  onOpenEvent: (id: string, step?: WorkflowStepKey | null) => void;
  onActionDone: () => void;
  sending: boolean;
  nothingSteps: WorkflowStepKey[];
  /** Inventory tài sản (đọc subjects): không còn mục chạy được ⇒ sang Duyệt. */
  assetsNothingToRun: boolean;
  /** Inventory hiện tại: video đang chờ bước trước (theo bước hiện tại). */
  blockedUpstream: Partial<Record<WorkflowStepKey, string[]>>;
  /** Tên từng mục tài sản (theo khoá) cho hàng cờ. */
  assetNames: Record<string, string>;
  error: ActionError | null;
  onCloseError: () => void;
  onReload: () => void;
  /** «Thử lại» của băng lỗi (CONFIRMATION_CHANGED: chưa chạy gì). */
  onRetry?: () => void;
  onRun: (call: PrimaryCall | null) => void;
  busyClipId: string | null;
  onSelectClip: (eventId: string, clipId: string) => void;
  /** U32b — video đang chọn ở ô gộp «Prompt & Video» (`?event=`), chọn video khác, và chuyển ô trên thanh bước (bỏ `?event=` khi rời ô gộp). */
  selectedEventId?: string | null;
  onSelectEvent?: (eventId: string) => void;
  onPickStep?: (step: WorkflowStepKey) => void;
}

const WorkflowBoard: React.FC<Props> = ({
  top, status, planName, planId, storyId, filter, setFilter, detail, detailLoading, detailError, onLoadDetail, storyboards, storyboardsError, maps, mapsError,
  eventsById, onOpenEvent, onActionDone, sending, nothingSteps, assetsNothingToRun, blockedUpstream, assetNames, error, onCloseError, onReload, onRetry, onRun, busyClipId, onSelectClip, selectedEventId, onSelectEvent, onPickStep,
}) => {
  const step = boardStepOf(status, filter.step);
  const opts = { sending, nothingToRun: nothingSteps, assetsNothingToRun, blockedUpstream };
  const primary = primaryActionOf(status, opts);
  // 261010 P9D D1 — sentinel 1 px ngay trên thanh bước (không observe chính thanh). Compact primary luôn mount (visibility). Hysteresis + debounce unstick 80 ms.
  const sentinelRef = useRef<HTMLDivElement>(null);
  const stickyRef = useRef<HTMLDivElement>(null);
  const stuckRef = useRef(false);
  const unstickTimer = useRef<number | null>(null);
  const scrollerRef = useRef<HTMLElement | null>(null);
  const [stuck, setStuck] = useState(false);
  const [stickyH, setStickyH] = useState(56);
  const [box, setBox] = useState({ top: 0, left: 0, width: 0 });
  useEffect(() => {
    // Thanh dính = position:fixed khớp mép vùng cuộn (không phủ sidebar/topbar): đo vùng cuộn, đo lại khi đổi cỡ cửa sổ.
    let p = sentinelRef.current?.parentElement ?? null;
    while (p && !/(auto|scroll)/.test(getComputedStyle(p).overflowY)) p = p.parentElement;
    if (!p) return undefined;
    const scroller = p;
    scrollerRef.current = scroller;
    const measure = () => {
      const r = scroller.getBoundingClientRect();
      setBox((b) => (b.top === r.top && b.left === r.left && b.width === scroller.clientWidth ? b : { top: r.top, left: r.left, width: scroller.clientWidth }));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  useEffect(() => {
    // H = chiều cao thanh LÚC KHÔNG dính (kể cả compact primary, đã chiếm chỗ bằng visibility) — chỉ đo khi chưa dính ⇒ spacer không đổi cỡ theo trạng thái dính.
    const el = stickyRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      if (stuckRef.current) return;
      const h = el.getBoundingClientRect().height;
      if (h > 0) setStickyH(h);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return undefined;
    // Geometry HIỆN TẠI của sentinel so với vùng cuộn thật (không dựa mẫu IO cũ): IO chỉ bắn khi băng ngưỡng nên cuộn lên tiếp lúc sentinel đã thấy hết sẽ không có mẫu mới.
    const sample = () => {
      const s = el.getBoundingClientRect();
      const sc = scrollerRef.current?.getBoundingClientRect();
      const view = sc ? { top: sc.top, bottom: sc.bottom } : { top: 0, bottom: window.innerHeight };
      return { ratio: sentinelRatioOf({ top: s.top, bottom: s.bottom }, view), top: s.top, edge: sc ? sc.top : 0, hyst: STICKY_HYST_PX };
    };
    const evaluate = () => {
      const next = stuckAfter({ ...sample(), wasStuck: stuckRef.current });
      if (next && !stuckRef.current) {
        if (unstickTimer.current) { window.clearTimeout(unstickTimer.current); unstickTimer.current = null; }
        stuckRef.current = true;
        setStuck(true);
        return;
      }
      if (next && stuckRef.current && unstickTimer.current) { window.clearTimeout(unstickTimer.current); unstickTimer.current = null; return; } // cuộn xuống lại trong lúc chờ nhả
      if (!next && stuckRef.current && !unstickTimer.current) {
        unstickTimer.current = window.setTimeout(() => {
          unstickTimer.current = null;
          if (!stuckAfter({ ...sample(), wasStuck: true })) { stuckRef.current = false; setStuck(false); } // kiểm lại geometry LÚC HẾT HẠN
        }, UNSTICK_DEBOUNCE_MS);
      }
    };
    let raf = 0;
    const onScroll = () => { if (raf) return; raf = window.requestAnimationFrame(() => { raf = 0; evaluate(); }); };
    const scroller = scrollerRef.current;
    const target: HTMLElement | Window = scroller ?? window;
    target.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    const io = typeof IntersectionObserver !== 'undefined' ? new IntersectionObserver(() => evaluate()) : null;
    io?.observe(el);
    evaluate();
    return () => {
      target.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      io?.disconnect();
      if (raf) window.cancelAnimationFrame(raf);
      if (unstickTimer.current) window.clearTimeout(unstickTimer.current);
    };
  }, []);
  return (
    <div className="wf">
      <WorkflowTopExtraContext.Provider value={{ meta: <WorkflowHero status={status} />, primary: <WorkflowPrimaryBar action={primary} sending={sending} onRun={onRun} /> }}>{top}</WorkflowTopExtraContext.Provider>
      <div ref={sentinelRef} className="wf-sticky-sentinel" data-testid="wf-sticky-sentinel" aria-hidden="true" />
      <StepErrorAlert error={error} onReload={onReload} onClose={onCloseError} onRetry={onRetry} />
      {stuck ? <div className="wf-sticky-spacer" style={{ height: stickyH }} data-testid="wf-sticky-spacer" aria-hidden="true" /> : null}
      <div
        ref={stickyRef}
        className={`wf-sticky${stuck ? ' stuck' : ''}`}
        style={stuck ? { top: box.top, left: box.left, width: box.width } : undefined}
        data-testid="wf-sticky"
      >
        <WorkflowStepsBar status={status} selected={step} onSelect={(k) => (onPickStep ? onPickStep(k) : setFilter({ step: k, state: 'AUTO' }))} />
        <WorkflowPrimaryBar action={primary} sending={sending} onRun={onRun} compact />
      </div>
      <WorkflowStepBoard
        status={status} planId={planId} storyId={storyId} step={step} stateFilter={filter.state} onStateFilter={(s) => setFilter((f) => ({ ...f, state: s }))}
        detail={detail} detailLoading={detailLoading} detailError={detailError} onLoadDetail={onLoadDetail}
        storyboards={storyboards} storyboardsError={storyboardsError} maps={maps} mapsError={mapsError} eventsById={eventsById}
        headerActions={stepHeaderActionsOf(status, step, opts)} primary={primary} assetNames={assetNames} sending={sending} onRun={onRun}
        onOpenEvent={onOpenEvent} onGoStep={(k) => setFilter({ step: k, state: 'AUTO' })} selectedEventId={selectedEventId} onSelectEvent={onSelectEvent} onActionDone={onActionDone} busyClipId={busyClipId} onSelectClip={onSelectClip}
      />
    </div>
  );
};

export default WorkflowBoard;
